import React, { useMemo, useState, useEffect } from 'react';
import { DollarSign, FileText, Activity, AlertCircle } from 'lucide-react';
import { QuickStatsCard } from '@shared/components/QuickStatsCard';
import { useCurrency } from '@shared/selectors/globalSelectors';
import { getLoginDataFromStorage } from '@shared/utils/loginUtils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@shared/ui/tooltip';
import { useConsumerBill } from '../../shared/hook';
import { useTranslation } from 'react-i18next';


export const KPIGrid = () => {
  const { t } = useTranslation();
  const currency = useCurrency();
  
  // Dynamic usage logic
  const { remoteUtilityId, remoteConsumerNumber, consumerId } = getLoginDataFromStorage();

  const { data: dashboardData } = useConsumerBill({
    remote_utility_id: remoteUtilityId,
    consumer_number: remoteConsumerNumber,
    is_bill_summary: true
  });

  const billData = dashboardData?.billData?.[0];  

  const getUnitForService = (service: string) => {
    if (!service) return 'Units';
    const s = service.toLowerCase();
    if (s.includes('electric') || s.includes('power')) return 'kWh';
    if (s.includes('gas')) return 'CCF';
    if (s.includes('water')) return 'Gal';
    return 'Units';
  };

  const activeUsages = useMemo(() => {
    const details = dashboardData?.consumerMappingData || [];
    return details.map((detail: any) => {
      const current = Number(detail?.meterDetails?.currentReading) || 0;
      const last = Number(detail?.meterDetails?.lastReading) || 0;
      return {
        name: detail?.utilityService,
        consumption: Math.max(0, current - last),
        unit: getUnitForService(detail?.utilityService)
      };
    });
  }, [dashboardData]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
      <QuickStatsCard 
        title={t("Balance Due")} 
        displayValue={`${currency} ${billData?.billAmount ?? '0'}`} 
        trendSummary={t("Due on {{date}}", { date: billData?.dueDate ?? t("NA") })}
        icon={
          <div className="p-2.5 rounded-xl bg-red-100 bg-opacity-20 flex items-center justify-center">
            <DollarSign size={22} className="text-red-600" />
          </div>
        }
      />
      <QuickStatsCard 
        title={t("Last Payment")} 
        displayValue={`${currency} ${billData?.lastPayAmount ?? t("NA")}`} 
        trendSummary={t("Received {{date}}", { date: billData?.lastPaymentDate ?? t("NA") })}
        icon={
          <div className="p-2.5 rounded-xl bg-green-100 bg-opacity-20 flex items-center justify-center">
            <FileText size={22} className="text-green-600" />
          </div>
        }
      />
      <QuickStatsCard 
        title={t("Current Usage")} 
        displayValue={
          <TooltipProvider>
            <Tooltip delayDuration={200}>
              <TooltipTrigger className="cursor-help border-b border-dashed border-slate-400 font-semibold text-slate-800">
                {t("{{count}} Active Services", { count: activeUsages.length })}
              </TooltipTrigger>
              <TooltipContent side="bottom" className="p-3 shadow-lg rounded-lg bg-white border border-slate-200">
                <div className="flex flex-col gap-2 text-sm font-semibold w-40">
                  <div className="text-xs text-slate-400 mb-1">{t("Usage Details")}</div>
                  {activeUsages.map((usage: any, idx: number) => (
                    <div key={idx} className="flex justify-between items-center">
                      <span className="text-slate-500 font-medium capitalize">{usage.name}</span>
                      <span className="text-slate-900">{usage.consumption} {usage.unit}</span>
                    </div>
                  ))}
                  {activeUsages.length === 0 && (
                    <div className="text-slate-500 text-xs text-center py-1">{t("No meter data")}</div>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        }
        trendSummary={t("Consumption this month")}
        icon={
          <div className="p-2.5 rounded-xl bg-blue-100 bg-opacity-20 flex items-center justify-center">
            <Activity size={22} className="text-blue-600" />
          </div>
        }
      />
      <QuickStatsCard 
        title={t("Pending Complaints")} 
        displayValue={dashboardData?.pendingComplaintsCount ?? t("NA")} 
        trendSummary={t("Low Pressure (In Progress)")}
        icon={
          <div className="p-2.5 rounded-xl bg-orange-100 bg-opacity-20 flex items-center justify-center">
            <AlertCircle size={22} className="text-orange-600" />
          </div>
        }
      />
    </div>
  );
};
